/**
 * Cot Playback Fixtures (dev only)
 *
 * Recorded / hand-authored PlatformEvent streams used by CotPlaybackPage to
 * drive the real CoT pipeline deterministically. See
 * context/plan/twinkling-leaping-pumpkin.md for the design.
 *
 * Every fixture stores events in CHRONOLOGICAL order (oldest first). The
 * page's reorder modes transform this array before feeding it to the
 * playback generator.
 */

import type { PlatformEvent } from '../../types/events';
import { COT_FIXTURE_KEY } from '../../utils/event-debug';
import paperAnalysisRun from './cot-playback-fixture-paper-analysis.json';
import hnAgentAnalysisRun from './cot-playback-fixture-hn-agent-analysis.json';

export interface PlaybackFixture {
  id: string;
  title: string;
  description: string;
  events: PlatformEvent[];
  /** Optional: runIds that are children of the parent run — used by the
   *  "starve one child" mode to decide which stream to defer. */
  childRunIds?: string[];
  /** Optional: the parent run id for sub-agent fixtures. */
  parentRunId?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

let _ts = 0;
/** Monotonic timestamp string used during fixture construction. */
function nextTs(): string {
  _ts += 100;
  return new Date(1_700_000_000_000 + _ts).toISOString();
}
function resetTs() {
  _ts = 0;
}

let _evtCounter = 0;
function evtId(): string {
  _evtCounter += 1;
  return `evt-fx-${_evtCounter.toString(36).padStart(6, '0')}`;
}

function mk(
  eventType: string,
  runId: string,
  data: Record<string, unknown>,
  parentRunId?: string,
): PlatformEvent {
  const timestamp = nextTs();
  return {
    eventId: evtId(),
    eventType,
    source: `agent:${runId}`,
    subjectType: 'run',
    subjectId: runId,
    data: {
      runId,
      ...(parentRunId ? { parentRunId } : {}),
      ...data,
      timestamp,
    },
    metadata: {
      timestamp,
      actorType: 'agent',
      actorId: runId,
    },
    orgId: 'org-test',
  };
}

// ─── Fixture 1: minimal-3-tool ──────────────────────────────────────────────

function buildMinimal3Tool(): PlatformEvent[] {
  resetTs();
  _evtCounter = 0;
  const runId = 'run-min-3tool';
  const events: PlatformEvent[] = [];

  events.push(
    mk('agent.run.started', runId, {
      agentId: 'agt-min-test',
      prompt: 'Run three example tools.',
      conversationId: 'con-min-test',
      userId: 'usr-test',
      agentName: 'Minimal Test Agent',
    }),
  );

  // Iteration 0: reason then decide to call tools
  events.push(mk('agent.run.reasoning_start', runId, { iteration: 0 }));
  events.push(
    mk('agent.run.reasoning_delta', runId, {
      iteration: 0,
      delta: "I'll run three example tools to demonstrate the CoT.",
      accumulated: "I'll run three example tools to demonstrate the CoT.",
    }),
  );
  events.push(mk('agent.run.reasoning_stop', runId, { iteration: 0, content: "I'll run three example tools to demonstrate the CoT." }));

  events.push(mk('agent.run.response_start', runId, { iteration: 0 }));
  events.push(
    mk('agent.run.response_stop', runId, {
      iteration: 0,
      isFinal: false,
      content: 'Running three tools in sequence.',
      usage: { inputTokens: 100, outputTokens: 15, totalTokens: 115 },
    }),
  );

  // Three sequential tool calls
  const tools = [
    { id: 'tc-min-1', name: 'Calculator', input: { expression: '2 + 2' }, output: '{"result":4}' },
    { id: 'tc-min-2', name: 'Search', input: { query: 'claude code' }, output: '{"hits":3}' },
    { id: 'tc-min-3', name: 'Files', input: { action: 'read', path: 'README.md' }, output: '{"bytes":512}' },
  ];
  for (const t of tools) {
    events.push(
      mk('agent.run.tool_call', runId, {
        iteration: 0,
        toolName: t.name,
        toolDisplayName: t.name,
        toolCallId: t.id,
        input: t.input,
      }),
    );
    events.push(
      mk('agent.run.tool_result', runId, {
        iteration: 0,
        toolName: t.name,
        toolDisplayName: t.name,
        toolCallId: t.id,
        success: true,
        output: t.output,
      }),
    );
  }

  // Iteration 1: final answer
  events.push(mk('agent.run.response_start', runId, { iteration: 1 }));
  events.push(
    mk('agent.run.response_stop', runId, {
      iteration: 1,
      isFinal: true,
      content: 'Wrapping up with a summary.',
      usage: { inputTokens: 140, outputTokens: 8, totalTokens: 148 },
    }),
  );
  events.push(mk('agent.run.final_answer_start', runId, { iteration: 1 }));
  events.push(
    mk('agent.run.final_answer_delta', runId, {
      iteration: 1,
      delta: 'I ran three tools: Calculator, Search, and Files. ',
      accumulated: 'I ran three tools: Calculator, Search, and Files. ',
    }),
  );
  events.push(
    mk('agent.run.final_answer_delta', runId, {
      iteration: 1,
      delta: 'All three completed successfully.',
      accumulated: 'I ran three tools: Calculator, Search, and Files. All three completed successfully.',
    }),
  );
  events.push(
    mk('agent.run.final_answer_stop', runId, {
      iteration: 1,
      content: 'I ran three tools: Calculator, Search, and Files. All three completed successfully.',
      usage: { inputTokens: 160, outputTokens: 24 },
    }),
  );
  events.push(
    mk('agent.run.completed', runId, {
      response: 'I ran three tools: Calculator, Search, and Files. All three completed successfully.',
      steps: [],
      durationMs: _ts,
      usage: { inputTokens: 160, outputTokens: 24, totalTokens: 184 },
    }),
  );

  return events;
}

// ─── Fixture 2: hn-analysis-run ─────────────────────────────────────────────
// The 43-event Agent Architect run the user pasted. Stored NEWEST-FIRST
// (matching the user's paste) and reversed to chronological at the bottom.
// Tool-call `output` strings are shortened to keep the fixture file readable
// without changing the event graph.

const HN_RUN_ID = 'run-01knz0xnphzc3hwfm9qq';
const HN_AGENT_ID = 'agt-01khej1cx4ve372xg6mk';
const HN_CONV_ID = 'con-01knszj4kexbr0qv5v7s';
const HN_ORG_ID = 'org-01kh0b7j4ctnnpkbcw8z';

function hnEvt(
  eventId: string,
  eventType: string,
  timestamp: string,
  data: Record<string, unknown>,
): PlatformEvent {
  return {
    eventId,
    eventType,
    source: `agent:${HN_AGENT_ID}`,
    subjectType: 'run',
    subjectId: HN_RUN_ID,
    data: {
      runId: HN_RUN_ID,
      agentId: HN_AGENT_ID,
      agentName: 'Agent Architect',
      ...data,
    },
    metadata: {
      timestamp,
      actorType: 'agent',
      actorId: HN_AGENT_ID,
    },
    orgId: HN_ORG_ID,
  };
}

// Pasted events in newest-first order (per user's sample). Reversed below.
// Outputs truncated to first ~200 chars to keep the file readable.
const HN_EVENTS_NEWEST_FIRST: PlatformEvent[] = [
  hnEvt('evt-01knz100ewsbwrcq7jn6', 'agent.run.completed', '2026-04-11T19:41:11.516Z', {
    durationMs: 75115,
    conversationId: HN_CONV_ID,
    response: '',
    steps: [],
    toolCallCount: 16,
    usage: { inputTokens: 234736, outputTokens: 3436, totalTokens: 238172 },
    contextUtilization: {
      inputTokens: 33966, contextLimit: 128000, utilizationPercent: 26.54,
      compactionThresholdPercent: 80, compactionImminent: false,
    },
  }),
  hnEvt('evt-01knz100bw3szbjjp7gf', 'agent.run.final_answer_stop', '2026-04-11T19:41:11.420Z', {
    iteration: 8,
    content:
      'The analysis is on the **HN Agent Run Analysis** surface. Key findings:\n\n**Deduplication is working.** The agent queried the SQLite DB with 10 candidate story IDs and found 2 that were already featured from the previous run (`47724352` and `47730194`). It excluded both and selected 3 new stories.\n\n3 new stories featured this run.',
    usage: { inputTokens: 33966, outputTokens: 297, totalTokens: 34263 },
  }),
  hnEvt('evt-01knz0zwm0ssas4r5gjy', 'agent.run.final_answer_start', '2026-04-11T19:41:07.584Z', { iteration: 8 }),
  hnEvt('evt-01knz0zrksqpvkj72p24', 'agent.run.surface_update', '2026-04-11T19:41:03.481Z', {
    canvasId: 'cvs-01knszj5d7z6h1hnpy5a',
    surfaceId: 'hn-run-analysis',
    title: 'HN Agent Run Analysis — Apr 11, 12:16 PM',
    spec: { updates: [] },
    version: 1,
    success: true,
    durationMs: 29,
    iteration: 7,
  }),
  hnEvt('evt-01knz0zrhgk27qbzeen8', 'agent.run.response_stop', '2026-04-11T19:41:03.408Z', {
    iteration: 7, isFinal: false, content: 'Now let me render the analysis.',
    usage: { inputTokens: 32249, outputTokens: 1411, totalTokens: 33660 },
  }),
  hnEvt('evt-01knz0z1c4vz3qe2vwq7', 'agent.run.response_start', '2026-04-11T19:40:39.684Z', { iteration: 7 }),
  // Iteration 6: batch of get_step tool calls
  hnEvt('evt-01knz0yw8fd0w4mnyxxe', 'agent.run.tool_result', '2026-04-11T19:40:34.447Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_SAF41iCbanHdjluJ3wfzBa', success: true,
    output: '{"index":33,"step":{"input":{"action":"sql","params":["47731506"]}}}',
    iteration: 6, durationMs: 16,
  }),
  hnEvt('evt-01knz0yw7ph5xwtykz1h', 'agent.run.tool_call', '2026-04-11T19:40:34.422Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_SAF41iCbanHdjluJ3wfzBa',
    input: { operation: 'get_step', stepIndex: 33, targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 6,
  }),
  hnEvt('evt-01knz0yw7cqcw6bbejfy', 'agent.run.tool_result', '2026-04-11T19:40:34.413Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_iES2S7BKmH02VFXfKUS5ak', success: true,
    output: '{"index":31,"step":{"input":{"action":"sql","params":["47732020"]}}}',
    iteration: 6, durationMs: 14,
  }),
  hnEvt('evt-01knz0yw6mvev6xxpppa', 'agent.run.tool_call', '2026-04-11T19:40:34.388Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_iES2S7BKmH02VFXfKUS5ak',
    input: { operation: 'get_step', stepIndex: 31, targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 6,
  }),
  hnEvt('evt-01knz0yw69t5tn6bqap8', 'agent.run.tool_result', '2026-04-11T19:40:34.377Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_JBbXYBzIDdpTXofnoz7mF7', success: true,
    output: '{"index":29,"step":{"output":{"activityId":"act-0001mxn7kh3qxrdd46jg"}}}',
    iteration: 6, durationMs: 17,
  }),
  hnEvt('evt-01knz0yw5dwbejt0bpp2', 'agent.run.tool_call', '2026-04-11T19:40:34.349Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_JBbXYBzIDdpTXofnoz7mF7',
    input: { operation: 'get_step', stepIndex: 29, targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 6,
  }),
  hnEvt('evt-01knz0yw4xqthgn0xd9r', 'agent.run.tool_result', '2026-04-11T19:40:34.334Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_F6Au4oi43W1wmdWq5IIRg2', success: true,
    output: '{"index":28,"step":{"text":"Now let me create the activity with all three stories:"}}',
    iteration: 6, durationMs: 16,
  }),
  hnEvt('evt-01knz0yw43sztqk7ggas', 'agent.run.tool_call', '2026-04-11T19:40:34.307Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_F6Au4oi43W1wmdWq5IIRg2',
    input: { operation: 'get_step', stepIndex: 28, targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 6,
  }),
  // Iteration 5
  hnEvt('evt-01knz0yp5xc6yb6fy7kg', 'agent.run.tool_result', '2026-04-11T19:40:28.221Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_yIiHEzWBUFiKzrmd57uLQi', success: true,
    output: '{"index":17,"step":{"input":{"action":"query","expression":"data.markdown"}}}',
    iteration: 5, durationMs: 15,
  }),
  hnEvt('evt-01knz0yp55jq87sq2k7k', 'agent.run.tool_call', '2026-04-11T19:40:28.197Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_yIiHEzWBUFiKzrmd57uLQi',
    input: { operation: 'get_step', stepIndex: 17, targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 5,
  }),
  hnEvt('evt-01knz0yp4vm6djehjfjb', 'agent.run.tool_result', '2026-04-11T19:40:28.187Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_SDBm849UDZIUIXCPTmKSmt', success: true,
    output: '{"index":16,"step":{"output":{"written":true,"path":"story1_comments.txt"}}}',
    iteration: 5, durationMs: 16,
  }),
  hnEvt('evt-01knz0yp42zw5yjxb0n5', 'agent.run.tool_call', '2026-04-11T19:40:28.162Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_SDBm849UDZIUIXCPTmKSmt',
    input: { operation: 'get_step', stepIndex: 16, targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 5,
  }),
  hnEvt('evt-01knz0yp3pynazye8swy', 'agent.run.tool_result', '2026-04-11T19:40:28.150Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_lWMJMOLC5mYtrmqiutgpeC', success: true,
    output: '{"index":7,"step":{"output":{"columns":["story_id"],"rows":[["47724352"],["47730194"]]}}}',
    iteration: 5, durationMs: 23,
  }),
  hnEvt('evt-01knz0yp1hg4bdka85n7', 'agent.run.tool_call', '2026-04-11T19:40:28.081Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_lWMJMOLC5mYtrmqiutgpeC',
    input: { operation: 'get_step', stepIndex: 7, targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 5,
  }),
  hnEvt('evt-01knz0yp14054wpw8nc0', 'agent.run.tool_result', '2026-04-11T19:40:28.068Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_r4Yvd1Wc6jLzl28ZkXz9pC', success: true,
    output: '{"index":5,"step":{"output":"[hacker news markdown page]"}}',
    iteration: 5, durationMs: 18,
  }),
  hnEvt('evt-01knz0ynzkchf0rdcz9m', 'agent.run.tool_call', '2026-04-11T19:40:28.019Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_r4Yvd1Wc6jLzl28ZkXz9pC',
    input: { operation: 'get_step', stepIndex: 5, targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 5,
  }),
  // Iteration 4
  hnEvt('evt-01knz0yfxv58j9q4exnm', 'agent.run.tool_result', '2026-04-11T19:40:21.819Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_iXGbUYclvOKqlRxk2eP04O', success: true,
    output: '{"index":3,"step":{"output":"[hacker news markdown]"}}',
    iteration: 4, durationMs: 18,
  }),
  hnEvt('evt-01knz0yfwyr7jvqsv1w7', 'agent.run.tool_call', '2026-04-11T19:40:21.790Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_iXGbUYclvOKqlRxk2eP04O',
    input: { operation: 'get_step', targetRunId: 'run-01knyzjt7zsr069ywj0b', stepIndex: 3 },
    iteration: 4,
  }),
  hnEvt('evt-01knz0yfwk78xec77adf', 'agent.run.tool_result', '2026-04-11T19:40:21.779Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_7hHt7niBVumOPRDXbF5thU', success: true,
    output: '{"index":1,"step":{"input":{"action":"sql","query":"CREATE TABLE IF NOT EXISTS featured_stories"}}}',
    iteration: 4, durationMs: 15,
  }),
  hnEvt('evt-01knz0yfvs1maqerkp37', 'agent.run.tool_call', '2026-04-11T19:40:21.753Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_7hHt7niBVumOPRDXbF5thU',
    input: { operation: 'get_step', targetRunId: 'run-01knyzjt7zsr069ywj0b', stepIndex: 1 },
    iteration: 4,
  }),
  hnEvt('evt-01knz0yfvde0e26v5xqk', 'agent.run.tool_result', '2026-04-11T19:40:21.741Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_oVcyD6WjsMZP5a6H0b14Es', success: true,
    output: '{"index":0,"step":{"text":"I\'ll fetch the current Hacker News front page"}}',
    iteration: 4, durationMs: 29,
  }),
  hnEvt('evt-01knz0yft5njyearczk2', 'agent.run.tool_call', '2026-04-11T19:40:21.701Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_oVcyD6WjsMZP5a6H0b14Es',
    input: { operation: 'get_step', targetRunId: 'run-01knyzjt7zsr069ywj0b', stepIndex: 0 },
    iteration: 4,
  }),
  hnEvt('evt-01knz0yfss81zv93ym9r', 'agent.run.response_stop', '2026-04-11T19:40:21.689Z', {
    iteration: 4, isFinal: false,
    content: 'Let me get the key steps — the DB initialization, deduplication query, story fetching, activity creation, and DB insert.',
    usage: { inputTokens: 25167, outputTokens: 354, totalTokens: 25521 },
  }),
  hnEvt('evt-01knz0ydskrx3dphqxpj', 'agent.run.response_start', '2026-04-11T19:40:19.636Z', { iteration: 4 }),
  // Iteration 3
  hnEvt('evt-01knz0ya6q7dn8mqscgq', 'agent.run.tool_result', '2026-04-11T19:40:15.959Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_8xdEzdTVjV96BeMXkRNlL3', success: true,
    output: '{"totalSteps":34,"steps":[...]}',
    iteration: 3, durationMs: 10,
  }),
  hnEvt('evt-01knz0ya61asxf91q9q7', 'agent.run.tool_call', '2026-04-11T19:40:15.937Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_8xdEzdTVjV96BeMXkRNlL3',
    input: { operation: 'list_steps', targetRunId: 'run-01knyzjt7zsr069ywj0b' },
    iteration: 3,
  }),
  // Iteration 2
  hnEvt('evt-01knz0y6f9se0xdrywcf', 'agent.run.tool_result', '2026-04-11T19:40:12.137Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_pmf1gunYUzEl9yM7pBl07Q', success: true,
    output: '{"runs":[{"runId":"run-01knyzjt7zsr069ywj0b","status":"completed"}]}',
    iteration: 2, durationMs: 18,
  }),
  hnEvt('evt-01knz0y6ec2d9d9c8xcb', 'agent.run.tool_call', '2026-04-11T19:40:12.108Z', {
    toolName: 'Run Viewer', toolId: 'tol-01kkspj0snck20adh5nb',
    toolCallId: 'tooluse_pmf1gunYUzEl9yM7pBl07Q',
    input: { operation: 'list', targetAgentId: 'agt-01kmqyap1357f3kkpmsf', limit: 5 },
    iteration: 2,
  }),
  // Iteration 1
  hnEvt('evt-01knz0y2b8p8p484425y', 'agent.run.tool_result', '2026-04-11T19:40:07.912Z', {
    toolName: 'Files', toolId: 'tol-01kmrwhfxshzyf36v2pd',
    toolCallId: 'tooluse_F17weRaymSI4FVoBupjwW9', success: true,
    output: '[{"name":"HN Summary Agent","id":"agt-01kmqyap1357f3kkpmsf"}]',
    iteration: 1, durationMs: 40,
  }),
  hnEvt('evt-01knz0y29gccmfy0a5ek', 'agent.run.tool_call', '2026-04-11T19:40:07.856Z', {
    toolName: 'Files', toolId: 'tol-01kmrwhfxshzyf36v2pd',
    toolCallId: 'tooluse_F17weRaymSI4FVoBupjwW9',
    input: { action: 'query', path: '.tool_outputs/tol-01khej1cpx6x7z3f1cjw-1775936402.json', expression: "agents[*]" },
    iteration: 1,
  }),
  // Iteration 0
  hnEvt('evt-01knz0xx45rx2fdxbya1', 'agent.run.tool_result', '2026-04-11T19:40:02.565Z', {
    toolName: 'Agent Editor', toolId: 'tol-01khej1cpx6x7z3f1cjw',
    toolCallId: 'tooluse_25MTHmfGiQ192Ti6N1JKhO', success: true,
    output: '{"agents":[{"agentId":"agt-01kh0b9vwp1aq2pns85q","name":"Pirate"}]}',
    iteration: 0, durationMs: 23,
  }),
  hnEvt('evt-01knz0xwz3yeqfqwbh8q', 'agent.run.tool_call', '2026-04-11T19:40:02.404Z', {
    toolName: 'Agent Editor', toolId: 'tol-01khej1cpx6x7z3f1cjw',
    toolCallId: 'tooluse_25MTHmfGiQ192Ti6N1JKhO',
    input: { operation: 'list' },
    iteration: 0,
  }),
  hnEvt('evt-01knz0xwyr5jpg9vc5p3', 'agent.run.response_stop', '2026-04-11T19:40:02.392Z', {
    iteration: 0, isFinal: false,
    content: 'Let me find the agent and its latest run.',
    usage: { inputTokens: 20013, outputTokens: 118, totalTokens: 20131 },
  }),
  hnEvt('evt-01knz0xvef6rja6ce1pn', 'agent.run.response_start', '2026-04-11T19:40:00.848Z', { iteration: 0 }),
  hnEvt('evt-01knz0xvdzqabps55map', 'agent.run.reasoning_stop', '2026-04-11T19:40:00.831Z', {
    iteration: 0,
    content: 'The user wants me to analyze the last run of the HN news agent. Let me find the agent first, then look at its recent runs.',
  }),
  hnEvt('evt-01knz0xv9r662sbw77mk', 'agent.run.reasoning_start', '2026-04-11T19:40:00.696Z', { iteration: 0 }),
  hnEvt('evt-01knz0xqdh152mmd8a96', 'agent.run.started', '2026-04-11T19:39:56.721Z', {
    prompt: 'can you analyze the last hn news agent run?',
    conversationId: HN_CONV_ID,
    userId: 'usr-01kh0b7j4bx8cyxxraa5',
  }),
];

function buildHnAnalysisRun(): PlatformEvent[] {
  // Reverse to chronological order (the pasted array is newest-first).
  return [...HN_EVENTS_NEWEST_FIRST].reverse();
}

// ─── Fixture 3: subagent-three-concurrent ───────────────────────────────────
// Parent run with 3 concurrent children. Child B runs longer and child C
// fails. Events are chronological.

function buildSubagentThreeConcurrent(): {
  events: PlatformEvent[];
  parentRunId: string;
  childRunIds: string[];
} {
  resetTs();
  _evtCounter = 0;

  const parentRunId = 'run-parent-test';
  const childA = 'run-child-a';
  const childB = 'run-child-b';
  const childC = 'run-child-c';
  const events: PlatformEvent[] = [];

  // Parent kickoff
  events.push(
    mk('agent.run.started', parentRunId, {
      agentId: 'agt-parent-test',
      prompt: 'Dispatch three specialists in parallel.',
      conversationId: 'con-sa-test',
      userId: 'usr-test',
      agentName: 'Orchestrator',
    }),
  );
  events.push(mk('agent.run.reasoning_start', parentRunId, { iteration: 0 }));
  events.push(
    mk('agent.run.reasoning_delta', parentRunId, {
      iteration: 0,
      delta: 'I will dispatch three specialists in parallel.',
      accumulated: 'I will dispatch three specialists in parallel.',
    }),
  );
  events.push(mk('agent.run.reasoning_stop', parentRunId, { iteration: 0 }));
  events.push(
    mk('agent.run.response_start', parentRunId, { iteration: 0 }),
  );
  events.push(
    mk('agent.run.response_stop', parentRunId, {
      iteration: 0,
      isFinal: false,
      content: 'Spawning sub-agents.',
    }),
  );

  // Real dispatch tool call (the LLM tool use that triggers sub-agents).
  // This should be filtered out by isSubAgentDispatchInput so it doesn't
  // appear as a duplicate "Running code" entry alongside __subagent_spawn.
  events.push(
    mk('agent.run.tool_call', parentRunId, {
      iteration: 0,
      toolCallId: 'tooluse_dispatch_sa',
      toolName: 'execute_tasks',
      toolDisplayName: 'Sub-Agents',
      input: {
        tasks: [
          { agentId: 'agt-research', prompt: 'Research topic X', timeoutMs: 60000 },
          { agentId: 'agt-analysis', prompt: 'Analyze topic X', timeoutMs: 60000 },
          { agentId: 'agt-summary', prompt: 'Summarize topic X', timeoutMs: 60000 },
        ],
      },
    }),
  );

  // subagent_invoked → subagent_returned (this fills the group)
  events.push(
    mk('agent.run.subagent_invoked', parentRunId, {
      iteration: 0,
      count: 3,
      mode: 'parallel',
      tasks: [
        { agentId: 'agt-research', prompt: 'Research topic X' },
        { agentId: 'agt-analysis', prompt: 'Analyze topic X' },
        { agentId: 'agt-summary', prompt: 'Summarize topic X' },
      ],
    }),
  );
  events.push(
    mk('agent.run.subagent_returned', parentRunId, {
      iteration: 0,
      count: 3,
      mode: 'parallel',
      success: true,
      durationMs: 50,
      tasks: [
        { taskId: 'tsk-a', childRunId: childA, agentId: 'agt-research', agentName: 'Research Agent', prompt: 'Research topic X' },
        { taskId: 'tsk-b', childRunId: childB, agentId: 'agt-analysis', agentName: 'Analysis Agent', prompt: 'Analyze topic X' },
        { taskId: 'tsk-c', childRunId: childC, agentId: 'agt-summary', agentName: 'Summary Agent', prompt: 'Summarize topic X' },
      ],
    }),
  );

  // All three started. `subagent_started` is emitted on the parent run's
  // subject but its `data.runId` points to the *child* — the reducer handler
  // at run-stream-state.ts:170-184 scans groups and looks up the child by
  // data.runId, so we preserve that shape exactly.
  const childInfo = [
    { runId: childA, taskId: 'tsk-a', agentId: 'agt-research', agentName: 'Research Agent' },
    { runId: childB, taskId: 'tsk-b', agentId: 'agt-analysis', agentName: 'Analysis Agent' },
    { runId: childC, taskId: 'tsk-c', agentId: 'agt-summary', agentName: 'Summary Agent' },
  ];
  for (const c of childInfo) {
    const timestamp = nextTs();
    events.push({
      eventId: evtId(),
      eventType: 'agent.run.subagent_started',
      source: `agent:${parentRunId}`,
      subjectType: 'run',
      subjectId: parentRunId,
      data: {
        runId: c.runId,
        parentRunId,
        taskId: c.taskId,
        agentId: c.agentId,
        agentName: c.agentName,
        timestamp,
      },
      metadata: { timestamp, actorType: 'agent', actorId: parentRunId },
      orgId: 'org-test',
    });
  }

  // Child progress events interleaved (all three running concurrently)
  // Each child emits: reasoning_start/delta/stop → response_start/stop → tool_call → tool_result → [tool_call → tool_result] → final_answer_start/delta/stop
  //
  // Helper for child events with parentRunId populated.
  const childEvt = (
    eventType: string,
    childRunId: string,
    data: Record<string, unknown>,
  ): PlatformEvent => mk(eventType, childRunId, data, parentRunId);

  // Round 1: all three start reasoning
  events.push(childEvt('agent.run.reasoning_start', childA, { iteration: 0 }));
  events.push(childEvt('agent.run.reasoning_start', childB, { iteration: 0 }));
  events.push(childEvt('agent.run.reasoning_start', childC, { iteration: 0 }));

  // Round 2: reasoning deltas
  events.push(
    childEvt('agent.run.reasoning_delta', childA, {
      iteration: 0,
      delta: 'Researching sources and references.',
      accumulated: 'Researching sources and references.',
    }),
  );
  events.push(
    childEvt('agent.run.reasoning_delta', childB, {
      iteration: 0,
      delta: 'Analyzing the data.',
      accumulated: 'Analyzing the data.',
    }),
  );
  events.push(
    childEvt('agent.run.reasoning_delta', childC, {
      iteration: 0,
      delta: 'Summarizing key points.',
      accumulated: 'Summarizing key points.',
    }),
  );
  events.push(childEvt('agent.run.reasoning_stop', childA, { iteration: 0 }));
  events.push(childEvt('agent.run.reasoning_stop', childB, { iteration: 0 }));
  events.push(childEvt('agent.run.reasoning_stop', childC, { iteration: 0 }));

  // Round 3: responses
  events.push(childEvt('agent.run.response_start', childA, { iteration: 0 }));
  events.push(childEvt('agent.run.response_start', childB, { iteration: 0 }));
  events.push(childEvt('agent.run.response_start', childC, { iteration: 0 }));
  events.push(
    childEvt('agent.run.response_stop', childA, {
      iteration: 0,
      isFinal: false,
      content: 'Calling Search tool.',
    }),
  );
  events.push(
    childEvt('agent.run.response_stop', childB, {
      iteration: 0,
      isFinal: false,
      content: 'Calling Calculator tool.',
    }),
  );
  events.push(
    childEvt('agent.run.response_stop', childC, {
      iteration: 0,
      isFinal: false,
      content: 'Calling Files tool.',
    }),
  );

  // Round 4: first tool call for each child
  events.push(
    childEvt('agent.run.tool_call', childA, {
      iteration: 0,
      toolName: 'Search',
      toolCallId: 'tc-a-1',
      input: { query: 'topic x references' },
    }),
  );
  events.push(
    childEvt('agent.run.tool_call', childB, {
      iteration: 0,
      toolName: 'Calculator',
      toolCallId: 'tc-b-1',
      input: { expression: '42 * 17' },
    }),
  );
  events.push(
    childEvt('agent.run.tool_call', childC, {
      iteration: 0,
      toolName: 'Files',
      toolCallId: 'tc-c-1',
      input: { action: 'read', path: 'notes.md' },
    }),
  );

  // Round 5: tool results (C fails)
  events.push(
    childEvt('agent.run.tool_result', childA, {
      iteration: 0,
      toolName: 'Search',
      toolCallId: 'tc-a-1',
      success: true,
      output: '{"hits":5}',
    }),
  );
  events.push(
    childEvt('agent.run.tool_result', childB, {
      iteration: 0,
      toolName: 'Calculator',
      toolCallId: 'tc-b-1',
      success: true,
      output: '{"result":714}',
    }),
  );
  events.push(
    childEvt('agent.run.tool_result', childC, {
      iteration: 0,
      toolName: 'Files',
      toolCallId: 'tc-c-1',
      success: false,
      error: 'File not found: notes.md',
    }),
  );

  // Child A finishes first (short runner)
  events.push(childEvt('agent.run.final_answer_start', childA, { iteration: 0 }));
  events.push(
    childEvt('agent.run.final_answer_delta', childA, {
      iteration: 0,
      delta: 'Found 5 references to topic X.',
      accumulated: 'Found 5 references to topic X.',
    }),
  );
  events.push(
    childEvt('agent.run.final_answer_stop', childA, {
      iteration: 0,
      content: 'Found 5 references to topic X.',
    }),
  );
  events.push({
    eventId: evtId(),
    eventType: 'agent.run.subagent_completed',
    source: `agent:${parentRunId}`,
    subjectType: 'run',
    subjectId: parentRunId,
    data: {
      runId: childA,
      parentRunId,
      taskId: 'tsk-a',
      agentId: 'agt-research',
      agentName: 'Research Agent',
      success: true,
      response: 'Found 5 references to topic X.',
      durationMs: 1200,
      toolCallCount: 1,
      usage: { inputTokens: 200, outputTokens: 30, totalTokens: 230 },
      timestamp: nextTs(),
    },
    metadata: { timestamp: nextTs(), actorType: 'agent', actorId: parentRunId },
    orgId: 'org-test',
  });

  // Child B runs longer — does a second tool call
  events.push(
    childEvt('agent.run.tool_call', childB, {
      iteration: 1,
      toolName: 'Calculator',
      toolCallId: 'tc-b-2',
      input: { expression: '714 / 2' },
    }),
  );
  events.push(
    childEvt('agent.run.tool_result', childB, {
      iteration: 1,
      toolName: 'Calculator',
      toolCallId: 'tc-b-2',
      success: true,
      output: '{"result":357}',
    }),
  );
  events.push(childEvt('agent.run.final_answer_start', childB, { iteration: 1 }));
  events.push(
    childEvt('agent.run.final_answer_delta', childB, {
      iteration: 1,
      delta: '42 * 17 = 714; half is 357.',
      accumulated: '42 * 17 = 714; half is 357.',
    }),
  );
  events.push(
    childEvt('agent.run.final_answer_stop', childB, {
      iteration: 1,
      content: '42 * 17 = 714; half is 357.',
    }),
  );
  events.push({
    eventId: evtId(),
    eventType: 'agent.run.subagent_completed',
    source: `agent:${parentRunId}`,
    subjectType: 'run',
    subjectId: parentRunId,
    data: {
      runId: childB,
      parentRunId,
      taskId: 'tsk-b',
      agentId: 'agt-analysis',
      agentName: 'Analysis Agent',
      success: true,
      response: '42 * 17 = 714; half is 357.',
      durationMs: 2300,
      toolCallCount: 2,
      usage: { inputTokens: 250, outputTokens: 40, totalTokens: 290 },
      timestamp: nextTs(),
    },
    metadata: { timestamp: nextTs(), actorType: 'agent', actorId: parentRunId },
    orgId: 'org-test',
  });

  // Child C failed the first tool call — emits a failed completion with no final answer
  events.push({
    eventId: evtId(),
    eventType: 'agent.run.subagent_completed',
    source: `agent:${parentRunId}`,
    subjectType: 'run',
    subjectId: parentRunId,
    data: {
      runId: childC,
      parentRunId,
      taskId: 'tsk-c',
      agentId: 'agt-summary',
      agentName: 'Summary Agent',
      success: false,
      error: 'File not found: notes.md',
      durationMs: 900,
      toolCallCount: 1,
      usage: { inputTokens: 180, outputTokens: 20, totalTokens: 200 },
      timestamp: nextTs(),
    },
    metadata: { timestamp: nextTs(), actorType: 'agent', actorId: parentRunId },
    orgId: 'org-test',
  });

  // Result for the real dispatch tool call
  events.push(
    mk('agent.run.tool_result', parentRunId, {
      iteration: 0,
      toolCallId: 'tooluse_dispatch_sa',
      toolName: 'execute_tasks',
      toolDisplayName: 'Sub-Agents',
      success: true,
      output: JSON.stringify({
        count: 3, mode: 'parallel', successCount: 2, failureCount: 1, totalDurationMs: 2300,
      }),
    }),
  );

  // Parent: subagents_all_returned
  events.push(
    mk('agent.run.subagents_all_returned', parentRunId, {
      successCount: 2,
      failureCount: 1,
      totalDurationMs: 2300,
      tasks: [
        { taskId: 'tsk-a', agentId: 'agt-research', agentName: 'Research Agent', success: true, durationMs: 1200 },
        { taskId: 'tsk-b', agentId: 'agt-analysis', agentName: 'Analysis Agent', success: true, durationMs: 2300 },
        { taskId: 'tsk-c', agentId: 'agt-summary', agentName: 'Summary Agent', success: false, durationMs: 900 },
      ],
    }),
  );

  // Parent wraps up
  events.push(mk('agent.run.reasoning_start', parentRunId, { iteration: 1 }));
  events.push(
    mk('agent.run.reasoning_delta', parentRunId, {
      iteration: 1,
      delta: 'Two sub-agents succeeded, one failed. Aggregating.',
      accumulated: 'Two sub-agents succeeded, one failed. Aggregating.',
    }),
  );
  events.push(mk('agent.run.reasoning_stop', parentRunId, { iteration: 1 }));
  events.push(mk('agent.run.response_start', parentRunId, { iteration: 1 }));
  events.push(
    mk('agent.run.response_stop', parentRunId, {
      iteration: 1,
      isFinal: true,
      content: 'Two of three sub-agents completed.',
    }),
  );
  events.push(mk('agent.run.final_answer_start', parentRunId, { iteration: 1 }));
  events.push(
    mk('agent.run.final_answer_delta', parentRunId, {
      iteration: 1,
      delta: 'Research found 5 references. Analysis computed 714/2=357. Summary failed: file not found.',
      accumulated: 'Research found 5 references. Analysis computed 714/2=357. Summary failed: file not found.',
    }),
  );
  events.push(
    mk('agent.run.final_answer_stop', parentRunId, {
      iteration: 1,
      content: 'Research found 5 references. Analysis computed 714/2=357. Summary failed: file not found.',
    }),
  );
  events.push(
    mk('agent.run.completed', parentRunId, {
      response: 'Research found 5 references. Analysis computed 714/2=357. Summary failed: file not found.',
      steps: [],
      durationMs: 3500,
      usage: { inputTokens: 900, outputTokens: 120, totalTokens: 1020 },
    }),
  );

  return { events, parentRunId, childRunIds: [childA, childB, childC] };
}

// ─── Fixture 3b: subagent-started-first ─────────────────────────────────────
// The LIVE agent-core lifecycle: the parent streams `subagent_started` (subject
// = the child run) followed directly by the child's progress events, with NO
// `subagent_invoked` / `subagent_returned` beforehand. Exercises the lazy
// `findOrCreateSubAgent` path — before the fix, every child event here was
// dropped. Mirrors the captured EventBridge dump for run-01kvc68368mvtkaptade.

function buildSubagentStartedFirst(): {
  events: PlatformEvent[];
  parentRunId: string;
  childRunIds: string[];
} {
  resetTs();
  _evtCounter = 0;

  const parentRunId = 'run-coordinator';
  const childA = 'run-searcher-a';
  const childB = 'run-searcher-b';
  const events: PlatformEvent[] = [];

  // Parent kickoff + a bit of reasoning, then it spawns searchers.
  events.push(
    mk('agent.run.started', parentRunId, {
      agentId: 'agt-coordinator',
      prompt: 'Find the latest research papers about peptides.',
      conversationId: 'con-sa-started-first',
      userId: 'usr-test',
      agentName: 'Coordinator',
    }),
  );
  events.push(mk('agent.run.reasoning_start', parentRunId, { iteration: 0 }));
  events.push(
    mk('agent.run.reasoning_delta', parentRunId, {
      iteration: 0,
      delta: 'Launching parallel Searcher sub-agents.',
      accumulated: 'Launching parallel Searcher sub-agents.',
    }),
  );
  events.push(mk('agent.run.reasoning_stop', parentRunId, { iteration: 0 }));

  // subagent_started — subject is the CHILD run id (as the live backend emits),
  // and there is NO subagent_invoked/returned first.
  const children = [
    { runId: childA, taskId: 'sat-a', agentName: 'Research Searcher' },
    { runId: childB, taskId: 'sat-b', agentName: 'Research Searcher' },
  ];
  for (const c of children) {
    const timestamp = nextTs();
    events.push({
      eventId: evtId(),
      eventType: 'agent.run.subagent_started',
      source: `agent:${c.runId}`,
      subjectType: 'run',
      subjectId: c.runId,
      data: {
        runId: c.runId,
        parentRunId,
        taskId: c.taskId,
        agentId: 'agt-research',
        agentName: c.agentName,
        timestamp,
      },
      metadata: { timestamp, actorType: 'agent', actorId: c.runId },
      orgId: 'org-test',
    });
  }

  const childEvt = (
    eventType: string,
    childRunId: string,
    data: Record<string, unknown>,
  ): PlatformEvent => mk(eventType, childRunId, data, parentRunId);

  // Interleaved child progress (reasoning → response → tool_call/result).
  for (const c of children) {
    events.push(childEvt('agent.run.reasoning_start', c.runId, { iteration: 0 }));
    events.push(
      childEvt('agent.run.reasoning_delta', c.runId, {
        iteration: 0,
        delta: 'Searching the literature.',
        accumulated: 'Searching the literature.',
      }),
    );
    events.push(childEvt('agent.run.reasoning_stop', c.runId, { iteration: 0 }));
    events.push(childEvt('agent.run.response_start', c.runId, { iteration: 0 }));
    events.push(
      childEvt('agent.run.response_stop', c.runId, {
        iteration: 0,
        isFinal: false,
        content: 'Running a web search.',
      }),
    );
    events.push(
      childEvt('agent.run.tool_call', c.runId, {
        iteration: 0,
        toolName: 'Web Search',
        toolDisplayName: 'Web Search',
        toolCallId: `tc-${c.runId}`,
        input: { query: 'peptide research 2025 2026' },
      }),
    );
    events.push(
      childEvt('agent.run.tool_result', c.runId, {
        iteration: 0,
        toolCallId: `tc-${c.runId}`,
        toolName: 'Web Search',
        success: true,
        output: '{"results":[{"title":"Peptide drug development"}]}',
        durationMs: 2200,
      }),
    );
  }

  // Children complete, then the parent is told all returned.
  for (const c of children) {
    events.push(
      mk('agent.run.subagent_completed', parentRunId, {
        runId: c.runId,
        parentRunId,
        taskId: c.taskId,
        agentId: 'agt-research',
        agentName: c.agentName,
        success: true,
        response: 'Found 5 relevant papers.',
        durationMs: 2500,
        toolCallCount: 1,
        usage: { inputTokens: 745, outputTokens: 754, totalTokens: 1499 },
      }),
    );
  }
  events.push(
    mk('agent.run.subagents_all_returned', parentRunId, {
      successCount: 2,
      failureCount: 0,
      totalDurationMs: 2500,
      tasks: children.map((c) => ({
        taskId: c.taskId,
        agentId: 'agt-research',
        agentName: c.agentName,
        success: true,
        durationMs: 2500,
      })),
    }),
  );

  // Parent wraps up with a final answer.
  events.push(mk('agent.run.final_answer_start', parentRunId, { iteration: 1 }));
  events.push(
    mk('agent.run.final_answer_delta', parentRunId, {
      iteration: 1,
      delta: 'Compiled 10 recent peptide papers from both searchers.',
      accumulated: 'Compiled 10 recent peptide papers from both searchers.',
    }),
  );
  events.push(
    mk('agent.run.final_answer_stop', parentRunId, {
      iteration: 1,
      content: 'Compiled 10 recent peptide papers from both searchers.',
    }),
  );
  events.push(
    mk('agent.run.completed', parentRunId, {
      response: 'Compiled 10 recent peptide papers from both searchers.',
      steps: [],
      durationMs: 4000,
      usage: { inputTokens: 900, outputTokens: 120, totalTokens: 1020 },
    }),
  );

  return { events, parentRunId, childRunIds: [childA, childB] };
}

// ─── Fixtures 4–6: in-progress collapse boundary ────────────────────────────
// Three short fixtures that stop at different points around the
// agent.run.final_answer_start boundary. Used to verify that the CoT
// collapses to "Thought for Xs" the instant final_answer_start fires —
// not earlier, not later.

/** Shared prefix: started → reasoning → response → 2× tool_call/tool_result. */
function buildInProgressPrefix(runId: string): PlatformEvent[] {
  resetTs();
  _evtCounter = 0;
  const events: PlatformEvent[] = [];

  events.push(
    mk('agent.run.started', runId, {
      agentId: 'agt-in-progress-test',
      prompt: 'Demonstrate in-progress CoT behaviour.',
      conversationId: 'con-in-progress-test',
      userId: 'usr-test',
      agentName: 'In-Progress Test Agent',
    }),
  );

  events.push(mk('agent.run.reasoning_start', runId, { iteration: 0 }));
  events.push(
    mk('agent.run.reasoning_delta', runId, {
      iteration: 0,
      delta: "I need to gather some data before answering.",
      accumulated: "I need to gather some data before answering.",
    }),
  );
  events.push(
    mk('agent.run.reasoning_stop', runId, {
      iteration: 0,
      content: "I need to gather some data before answering.",
    }),
  );

  events.push(mk('agent.run.response_start', runId, { iteration: 0 }));
  events.push(
    mk('agent.run.response_stop', runId, {
      iteration: 0,
      isFinal: false,
      content: 'Let me look that up.',
      usage: { inputTokens: 80, outputTokens: 8, totalTokens: 88 },
    }),
  );

  const tools = [
    { id: 'tc-ip-1', name: 'Search', input: { query: 'q1' }, output: '{"hits":2}' },
    { id: 'tc-ip-2', name: 'Files', input: { action: 'read', path: 'a.md' }, output: '{"bytes":256}' },
  ];
  for (const t of tools) {
    events.push(
      mk('agent.run.tool_call', runId, {
        iteration: 0,
        toolName: t.name,
        toolDisplayName: t.name,
        toolCallId: t.id,
        input: t.input,
      }),
    );
    events.push(
      mk('agent.run.tool_result', runId, {
        iteration: 0,
        toolName: t.name,
        toolDisplayName: t.name,
        toolCallId: t.id,
        success: true,
        output: t.output,
      }),
    );
  }

  return events;
}

/** (a) Stops mid-run, no final_answer_*. CoT must stay expanded. */
function buildInProgressNoFinal(): PlatformEvent[] {
  return buildInProgressPrefix('run-ip-no-final');
}

/** (b) Same as (a) + final_answer_start. CoT must collapse, no body text. */
function buildFinalAnswerStartedNoText(): PlatformEvent[] {
  const runId = 'run-ip-fa-start';
  const events = buildInProgressPrefix(runId);
  events.push(mk('agent.run.final_answer_start', runId, { iteration: 1 }));
  return events;
}

/** (c) Same as (b) + several deltas (no final_answer_stop, no completed).
 *  CoT collapsed; answer text streams below the button. */
function buildFinalAnswerStreaming(): PlatformEvent[] {
  const runId = 'run-ip-fa-stream';
  const events = buildInProgressPrefix(runId);
  events.push(mk('agent.run.final_answer_start', runId, { iteration: 1 }));
  const chunks = [
    'Based on the lookups, ',
    'the answer is straightforward: ',
    'the data shows two relevant results.',
  ];
  let acc = '';
  for (const delta of chunks) {
    acc += delta;
    events.push(
      mk('agent.run.final_answer_delta', runId, {
        iteration: 1,
        delta,
        accumulated: acc,
      }),
    );
  }
  return events;
}

// ─── Fixture 7: operator-delegate-final-only ────────────────────────────────
// Real run pasted by the user (run-01kr6rhtd2zx8kmckwfg, "Operator One").
// Notable shape: 8 events, ZERO tool_calls, ZERO reasoning_*, ZERO
// final_answer_delta — content is delivered exclusively via
// final_answer_stop.content. Iterations 0 and 1 each emit a response_stop
// with isFinal:true (finalAnswerEnforcementRetries=2 in the completed event)
// before iteration 2 finally produces final_answer_*.
//
// Stored newest-first as pasted; reversed to chronological at the bottom.

const OP_DELEGATE_RAW: PlatformEvent[] = [
  {
    eventId: 'evt-01kr6rj20knshgfs92gq',
    eventType: 'agent.run.completed',
    source: 'agent:agt-01kpwf364snmdjy1bqxh',
    subjectType: 'run',
    subjectId: 'run-01kr6rhtd2zx8kmckwfg',
    data: {
      runId: 'run-01kr6rhtd2zx8kmckwfg',
      agentId: 'agt-01kpwf364snmdjy1bqxh',
      conversationId: 'con-01kqegm53qyq7rqb6psf',
      durationMs: 7261,
      agentName: 'Operator One',
      toolCallCount: 0,
      usage: { inputTokens: 9, outputTokens: 163, totalTokens: 172, cacheReadInputTokens: 22234, cacheWriteInputTokens: 34898 },
      contextUtilization: { inputTokens: 3, contextLimit: 200000, utilizationPercent: 0, compactionThresholdPercent: 80, compactionImminent: false },
      finalAnswerEnforcementRetries: 2,
      responsePreview: 'Delegated to the **Ops Analyst** to pull the latest 5 Signatera LIMS cases. Results will appear in a canvas shortly.',
    },
    metadata: { timestamp: '2026-05-09T16:19:04.851Z', actorType: 'agent', actorId: 'agt-01kpwf364snmdjy1bqxh' },
    orgId: 'org-01kp4wr3fqxfa13vbxyt',
  },
  {
    eventId: 'evt-01kr6rj1zz12f0pvr8nn',
    eventType: 'agent.run.final_answer_stop',
    source: 'agent:agt-01kpwf364snmdjy1bqxh',
    subjectType: 'run',
    subjectId: 'run-01kr6rhtd2zx8kmckwfg',
    data: {
      runId: 'run-01kr6rhtd2zx8kmckwfg',
      agentId: 'agt-01kpwf364snmdjy1bqxh',
      conversationId: 'con-01kqegm53qyq7rqb6psf',
      iteration: 2,
      content: 'Delegated to the **Ops Analyst** to pull the latest 5 Signatera LIMS cases. Results will appear in a canvas shortly.',
      usage: { inputTokens: 3, outputTokens: 87, totalTokens: 90 },
      contextUtilization: { inputTokens: 3, contextLimit: 200000, utilizationPercent: 0, compactionThresholdPercent: 80, compactionImminent: false },
      agentName: 'Operator One',
    },
    metadata: { timestamp: '2026-05-09T16:19:04.831Z', actorType: 'agent', actorId: 'agt-01kpwf364snmdjy1bqxh' },
    orgId: 'org-01kp4wr3fqxfa13vbxyt',
  },
  {
    eventId: 'evt-01kr6rj1dc8tgg3nn002',
    eventType: 'agent.run.final_answer_start',
    source: 'agent:agt-01kpwf364snmdjy1bqxh',
    subjectType: 'run',
    subjectId: 'run-01kr6rhtd2zx8kmckwfg',
    data: {
      runId: 'run-01kr6rhtd2zx8kmckwfg',
      agentId: 'agt-01kpwf364snmdjy1bqxh',
      conversationId: 'con-01kqegm53qyq7rqb6psf',
      iteration: 2,
      agentName: 'Operator One',
    },
    metadata: { timestamp: '2026-05-09T16:19:04.236Z', actorType: 'agent', actorId: 'agt-01kpwf364snmdjy1bqxh' },
    orgId: 'org-01kp4wr3fqxfa13vbxyt',
  },
  {
    eventId: 'evt-01kr6rhzb37hm2haeqr8',
    eventType: 'agent.run.response_stop',
    source: 'agent:agt-01kpwf364snmdjy1bqxh',
    subjectType: 'run',
    subjectId: 'run-01kr6rhtd2zx8kmckwfg',
    data: {
      runId: 'run-01kr6rhtd2zx8kmckwfg',
      agentId: 'agt-01kpwf364snmdjy1bqxh',
      conversationId: 'con-01kqegm53qyq7rqb6psf',
      iteration: 1,
      isFinal: true,
      content: 'Delegated to the **Ops Analyst** to pull the latest 5 Signatera LIMS cases. Results will appear in a canvas shortly.',
      usage: { inputTokens: 3, outputTokens: 38, totalTokens: 41 },
      contextUtilization: { inputTokens: 3, contextLimit: 200000, utilizationPercent: 0, compactionThresholdPercent: 80, compactionImminent: false },
      agentName: 'Operator One',
    },
    metadata: { timestamp: '2026-05-09T16:19:02.115Z', actorType: 'agent', actorId: 'agt-01kpwf364snmdjy1bqxh' },
    orgId: 'org-01kp4wr3fqxfa13vbxyt',
  },
  {
    eventId: 'evt-01kr6rhz2y3kwd92cjmy',
    eventType: 'agent.run.response_start',
    source: 'agent:agt-01kpwf364snmdjy1bqxh',
    subjectType: 'run',
    subjectId: 'run-01kr6rhtd2zx8kmckwfg',
    data: {
      runId: 'run-01kr6rhtd2zx8kmckwfg',
      agentId: 'agt-01kpwf364snmdjy1bqxh',
      conversationId: 'con-01kqegm53qyq7rqb6psf',
      iteration: 1,
      agentName: 'Operator One',
    },
    metadata: { timestamp: '2026-05-09T16:19:01.854Z', actorType: 'agent', actorId: 'agt-01kpwf364snmdjy1bqxh' },
    orgId: 'org-01kp4wr3fqxfa13vbxyt',
  },
  {
    eventId: 'evt-01kr6rhx81khy0p00t35',
    eventType: 'agent.run.response_stop',
    source: 'agent:agt-01kpwf364snmdjy1bqxh',
    subjectType: 'run',
    subjectId: 'run-01kr6rhtd2zx8kmckwfg',
    data: {
      runId: 'run-01kr6rhtd2zx8kmckwfg',
      agentId: 'agt-01kpwf364snmdjy1bqxh',
      conversationId: 'con-01kqegm53qyq7rqb6psf',
      iteration: 0,
      isFinal: true,
      content: 'Delegated to the **Ops Analyst** to pull the latest 5 Signatera LIMS cases. Results will appear in a canvas shortly.',
      usage: { inputTokens: 3, outputTokens: 38, totalTokens: 41 },
      contextUtilization: { inputTokens: 3, contextLimit: 200000, utilizationPercent: 0, compactionThresholdPercent: 80, compactionImminent: false },
      agentName: 'Operator One',
    },
    metadata: { timestamp: '2026-05-09T16:18:59.969Z', actorType: 'agent', actorId: 'agt-01kpwf364snmdjy1bqxh' },
    orgId: 'org-01kp4wr3fqxfa13vbxyt',
  },
  {
    eventId: 'evt-01kr6rhwxd20zz82k4kp',
    eventType: 'agent.run.response_start',
    source: 'agent:agt-01kpwf364snmdjy1bqxh',
    subjectType: 'run',
    subjectId: 'run-01kr6rhtd2zx8kmckwfg',
    data: {
      runId: 'run-01kr6rhtd2zx8kmckwfg',
      agentId: 'agt-01kpwf364snmdjy1bqxh',
      conversationId: 'con-01kqegm53qyq7rqb6psf',
      iteration: 0,
      agentName: 'Operator One',
    },
    metadata: { timestamp: '2026-05-09T16:18:59.629Z', actorType: 'agent', actorId: 'agt-01kpwf364snmdjy1bqxh' },
    orgId: 'org-01kp4wr3fqxfa13vbxyt',
  },
  {
    eventId: 'evt-01kr6rhv33jd8ygehy38',
    eventType: 'agent.run.started',
    source: 'agent:agt-01kpwf364snmdjy1bqxh',
    subjectType: 'run',
    subjectId: 'run-01kr6rhtd2zx8kmckwfg',
    data: {
      runId: 'run-01kr6rhtd2zx8kmckwfg',
      agentId: 'agt-01kpwf364snmdjy1bqxh',
      conversationId: 'con-01kqegm53qyq7rqb6psf',
      prompt: 'can you get the latest 5 signatera lims cases?',
      userId: 'usr-01kqk16b2nm448xnzfe0',
      agentName: 'Operator One',
    },
    metadata: { timestamp: '2026-05-09T16:18:57.764Z', actorType: 'agent', actorId: 'agt-01kpwf364snmdjy1bqxh' },
    orgId: 'org-01kp4wr3fqxfa13vbxyt',
  },
];

function buildOperatorDelegateFinalOnly(): PlatformEvent[] {
  return [...OP_DELEGATE_RAW].reverse();
}

// ─── Fixture 8: paper-analysis-architect ────────────────────────────────────
// Real Agent Architect run (run-01kr7h1ew0z8r7san9yv) analysing the AI Paper
// Daily Scout. 85 events, ~2m 39s. Mix of Run Viewer / Files / Get Activity /
// Agent Editor / List User Activities tool calls, skill activate+deactivate,
// canvas_created + surface_update, ending with final_answer_*. Loaded from
// JSON since the surface_update spec alone is ~150 lines.

/** Strip delegation markers from event data so the reducer treats the run
 *  as a coordinator (top-level) run rather than as sub-agent progress.
 *  Required for fixtures built from runs that were themselves delegated —
 *  the architect's own events carry parentRunId pointing at the user's
 *  coordinator, but for replay we want them treated as a main run. */
function stripDelegationMarkers(events: PlatformEvent[]): PlatformEvent[] {
  return events.map((evt) => {
    const data = evt.data as Record<string, unknown> | undefined;
    if (!data) return evt;
    if (data.parentRunId == null && data.executionMode == null && data.delegatedConversationId == null) {
      return evt;
    }
    const { parentRunId: _p, executionMode: _e, delegatedConversationId: _d, ...rest } = data;
    return { ...evt, data: rest };
  });
}

function buildPaperAnalysisArchitect(): PlatformEvent[] {
  return stripDelegationMarkers([...(paperAnalysisRun.events as PlatformEvent[])].reverse());
}

// ─── Fixture 9: hn-agent-analysis ───────────────────────────────────────────
// Real Agent Architect run (run-01kr7j09csmqg5zt99q7) analysing the HN
// Summary Agent's last run. 25 events, ~52s. 9 Run Viewer tool calls,
// reasoning_start/stop, surface_update with a multi-card analysis spec, and
// final_answer_*. Mid-size trace with the canonical happy-path shape.

function buildHnAgentAnalysis(): PlatformEvent[] {
  return [...(hnAgentAnalysisRun.events as PlatformEvent[])].reverse();
}

// ─── Fixture 10: task-list-demo ─────────────────────────────────────────────
// Synthetic run that drives the conversation task list (below the CoT). Uses
// the raw `task.*` vocabulary (task.started / task.progress / task.completed)
// from the agent log, interleaved with a little CoT so both fill in together.
// Mirrors where the real run cut off: tasks 1–6 done, 7 working, 8–9 open —
// so all three visual states render at once. No agent.run.completed, so the
// run stays "in progress" and task 7 keeps pulsing.

/** Emits a raw `task.*` event carrying runId (via mk) for filter matching. */
function taskEvt(
  eventType: 'task.started' | 'task.progress' | 'task.completed',
  runId: string,
  data: Record<string, unknown>,
): PlatformEvent {
  return mk(eventType, runId, { agentId: 'agt-task-demo', ...data });
}

function buildTaskListDemo(): PlatformEvent[] {
  resetTs();
  _evtCounter = 0;
  const runId = 'run-task-demo';
  const events: PlatformEvent[] = [];

  const titles = [
    'Create feature branch AICC-633-occ-route-by-intent',
    'Add TES types and BCP-47 locale helper',
    'Add routeToQueueByIntent to TesService',
    'Add payload builder isolating open decisions',
    'Wire OCC route-by-intent into gen2 + gen3 escalation behind flag',
    'Add config + WireMock stub',
    'Write unit tests',
    'Build, lint, run tests',
    'Show git diff in a new canvas',
  ];
  const taskId = (i: number) => String(i + 1);

  events.push(
    mk('agent.run.started', runId, {
      agentId: 'agt-task-demo',
      prompt: 'Implement AICC-633 behind a feature flag across gen2 + gen3.',
      conversationId: 'con-task-demo',
      userId: 'usr-test',
      agentName: 'Claude Code Agent',
    }),
  );

  // Brief CoT: plan the work, then lay out the task list.
  events.push(mk('agent.run.reasoning_start', runId, { iteration: 0 }));
  events.push(
    mk('agent.run.reasoning_delta', runId, {
      iteration: 0,
      delta: 'Decisions are locked. Let me set up the task list and start executing.',
      accumulated: 'Decisions are locked. Let me set up the task list and start executing.',
    }),
  );
  events.push(
    mk('agent.run.reasoning_stop', runId, {
      iteration: 0,
      content: 'Decisions are locked. Let me set up the task list and start executing.',
    }),
  );

  // Create all nine tasks up front (all open).
  titles.forEach((title, i) => {
    events.push(taskEvt('task.started', runId, { taskId: taskId(i), description: title }));
  });

  events.push(mk('agent.run.response_start', runId, { iteration: 0 }));
  events.push(
    mk('agent.run.response_stop', runId, {
      iteration: 0,
      isFinal: false,
      content: 'Working through the task list.',
    }),
  );

  // Execute tasks 1–6: progress (working) → completed (done). Sprinkle a couple
  // of tool calls so the CoT timeline shows real activity alongside the list.
  for (let i = 0; i < 6; i++) {
    events.push(
      taskEvt('task.progress', runId, {
        taskId: taskId(i),
        progress: 50,
        currentStep: titles[i],
      }),
    );
    if (i === 0) {
      events.push(
        mk('agent.run.tool_call', runId, {
          iteration: 0,
          toolName: 'Bash',
          toolDisplayName: 'Bash',
          toolCallId: 'tc-branch',
          input: { command: 'git checkout -b AICC-633-occ-route-by-intent' },
        }),
      );
      events.push(
        mk('agent.run.tool_result', runId, {
          iteration: 0,
          toolName: 'Bash',
          toolCallId: 'tc-branch',
          success: true,
          output: "Switched to a new branch 'AICC-633-occ-route-by-intent'",
        }),
      );
    }
    events.push(
      taskEvt('task.completed', runId, {
        taskId: taskId(i),
        summary: `${titles[i]} — done`,
      }),
    );
  }

  // Task 7 is currently working (stays pulsing — no completed event).
  events.push(
    taskEvt('task.progress', runId, {
      taskId: taskId(6),
      progress: 50,
      currentStep: 'Running jest suites…',
    }),
  );
  // Tasks 8 and 9 remain open (task.started only, already emitted above).

  return events;
}

// ─── Fixture: max-iterations failure ────────────────────────────────────────

/** Run that burns its iteration budget on tool calls and terminates via
 *  agent.run.failed with code MAX_ITERATIONS — no final_answer_*, no
 *  completed. Drives the ErrorAwareText "Iteration limit reached" alert. */
function buildMaxIterationsFailure(): PlatformEvent[] {
  resetTs();
  _evtCounter = 0;
  const runId = 'run-fx-maxiter-000001';
  const events: PlatformEvent[] = [];

  events.push(mk('agent.run.started', runId, { prompt: 'Bootstrap the catalog scaffolding.' }));

  const tools = [
    { id: 'tc-mi-1', name: 'Files', input: { action: 'write', path: 'known-issues/_catalog.md' } },
    { id: 'tc-mi-2', name: 'Files', input: { action: 'write', path: 'request-sops/_catalog.md' } },
  ];
  tools.forEach((t, i) => {
    events.push(
      mk('agent.run.response_stop', runId, {
        iteration: i,
        isFinal: false,
        content: `Writing file ${i + 1} of the scaffolding.`,
        usage: { inputTokens: 100 + i * 40, outputTokens: 12, totalTokens: 112 + i * 40 },
      }),
    );
    events.push(
      mk('agent.run.tool_call', runId, {
        iteration: i,
        toolName: t.name,
        toolDisplayName: t.name,
        toolCallId: t.id,
        input: t.input,
      }),
    );
    events.push(
      mk('agent.run.tool_result', runId, {
        iteration: i,
        toolName: t.name,
        toolDisplayName: t.name,
        toolCallId: t.id,
        success: true,
        output: '{"written":true}',
      }),
    );
  });

  events.push(
    mk('agent.run.failed', runId, {
      error:
        "Agent stopped after reaching the maximum of 2 iterations without producing a final answer. Work already done by tool calls is preserved. Ask the agent to continue, or raise the agent's max-iterations setting.",
      code: 'MAX_ITERATIONS',
      durationMs: _ts,
    }),
  );

  return events;
}

// ─── Fixture: ask_user parked runs (incident 2026-07-08) ────────────────────
// The runtime parks an ask_user run by emitting ask_user_requested and then a
// trailing agent.run.completed (+ final_answer_* restating the question);
// REST also reports the parked run as completed. Two runs in one
// conversation: ask #1 is answered, the follow-up run parks on ask #2 with
// FOUR questions — the exact shape whose second card never rendered in prod.

function buildAskUserParked(): PlatformEvent[] {
  resetTs();
  _evtCounter = 0;
  const conversationId = 'con-fx-askuser';
  const events: PlatformEvent[] = [];

  const ask = (
    runId: string,
    aur: string,
    questions: Array<Record<string, unknown>>,
  ) => {
    const questionText = (questions[0] as { question: string }).question;
    events.push(
      mk('agent.run.ask_user_requested', runId, {
        conversationId,
        askUserRequestId: aur,
        inboxItemId: `ibx-${aur}`,
        agentId: 'agt-fx-planner',
        agentName: 'Claude Code Planner',
        toolUseId: `tu-${aur}`,
        questions,
        iteration: 0,
      }),
    );
    // The parking completed — response is the QUESTION text, not an answer.
    events.push(
      mk('agent.run.completed', runId, {
        conversationId,
        response: questionText,
        durationMs: 30_458,
        usage: { inputTokens: 2_000, outputTokens: 300, totalTokens: 2_300 },
      }),
    );
    events.push(mk('agent.run.final_answer_start', runId, { conversationId, iteration: 0 }));
    events.push(
      mk('agent.run.final_answer_delta', runId, {
        conversationId,
        iteration: 0,
        accumulated: questionText,
        delta: questionText,
      }),
    );
    events.push(mk('agent.run.final_answer_stop', runId, { conversationId, iteration: 0 }));
  };

  // Run 1 — asks one question, gets answered.
  const run1 = 'run-fx-askuser-000001';
  events.push(
    mk('agent.run.started', run1, {
      conversationId,
      agentId: 'agt-fx-planner',
      agentName: 'Claude Code Planner',
      prompt: 'Analyze the ticket and create a plan.',
    }),
  );
  events.push(
    mk('agent.run.reasoning_delta', run1, {
      conversationId,
      iteration: 0,
      accumulated: 'The ticket references a repo I cannot see — I should ask.',
    }),
  );
  ask(run1, 'aur-fx-1', [
    {
      questionId: 'q1',
      question: 'Which repository should the plan target?',
      inputType: 'text',
      header: 'Repository',
    },
  ]);
  events.push(
    mk('agent.run.ask_user_answered', run1, {
      conversationId,
      askUserRequestId: 'aur-fx-1',
      answers: [{ questionId: 'q1', value: 'https://gitlab.example.com/eng/ai-call-center' }],
    }),
  );

  // Run 2 — the follow-up run parks on FOUR questions (the incident shape).
  const run2 = 'run-fx-askuser-000002';
  events.push(
    mk('agent.run.started', run2, {
      conversationId,
      agentId: 'agt-fx-planner',
      agentName: 'Claude Code Planner',
      prompt: '(ask_user answer) https://gitlab.example.com/eng/ai-call-center',
    }),
  );
  events.push(
    mk('agent.run.reasoning_delta', run2, {
      conversationId,
      iteration: 0,
      accumulated: 'Spec and ticket disagree on two fields — I need four clarifications.',
    }),
  );
  events.push(
    mk('agent.run.tool_call', run2, {
      conversationId,
      iteration: 0,
      toolName: 'Fetch',
      toolDisplayName: 'Fetch',
      toolCallId: 'tc-fx-au-1',
      input: { url: 'https://tes.example.com/openapi.json' },
    }),
  );
  events.push(
    mk('agent.run.tool_result', run2, {
      conversationId,
      iteration: 0,
      toolName: 'Fetch',
      toolDisplayName: 'Fetch',
      toolCallId: 'tc-fx-au-1',
      success: true,
      output: '{"spec":"v3"}',
    }),
  );
  ask(run2, 'aur-fx-2', [
    {
      questionId: 'q1',
      question:
        'The ticket says onshore/offshore is a boolean; the live spec uses a three-value status. Which is the source of truth?',
      inputType: 'select',
      header: 'Field contract',
      options: [
        { label: 'Follow live spec', description: 'Three-value shore status, optional DOB.' },
        { label: 'Follow ticket body', description: 'Boolean onshore flag, required DOB.' },
      ],
    },
    {
      questionId: 'q2',
      question: 'Should the rollout be gated behind a feature flag?',
      inputType: 'confirm',
      header: 'Rollout',
    },
    {
      questionId: 'q3',
      question: 'Does the Spanish locale need to ship in the same release?',
      inputType: 'confirm',
      header: 'Spanish locale',
    },
    {
      questionId: 'q4',
      question: 'How should unknown shore status render in the agent UI?',
      inputType: 'text',
      header: 'Shore status',
    },
  ]);

  return events;
}

// ─── Exported fixture list ──────────────────────────────────────────────────

const saFixture = buildSubagentThreeConcurrent();
const saStartedFirstFixture = buildSubagentStartedFirst();

export const FIXTURES: PlaybackFixture[] = [
  {
    id: 'task-list-demo',
    title: 'Task list — open / working / done',
    description:
      'Synthetic run driving the conversation task list below the CoT via raw task.* events. Creates 9 tasks, then completes 1–6, leaves 7 working (pulsing) and 8–9 open — all three status styles at once. Step or Play to watch tasks transition live.',
    events: buildTaskListDemo(),
  },
  {
    id: 'minimal-3-tool',
    title: 'Minimal — 3 sequential tools',
    description: 'Synthetic single-run: reasoning → 3 tool calls → final answer. Baseline for verifying the playback UI itself.',
    events: buildMinimal3Tool(),
  },
  {
    id: 'hn-analysis-run',
    title: 'HN analysis — 43 events',
    description: "The Agent Architect run the user pasted (run-01knz0xnphzc3hwfm9qq). 16 Run Viewer tool calls across 8 iterations. The run that exhibits the 'clear / duplicate steps' bug.",
    events: buildHnAnalysisRun(),
  },
  {
    id: 'subagent-three-concurrent',
    title: 'Sub-agents — 3 concurrent children',
    description: 'Synthetic parent with 3 parallel children. Child A finishes fastest, child B runs longer, child C fails. Use the "interleave sub-agents" / "starve one child" / "defer child events" modes to stress the sub-agent reducer.',
    events: saFixture.events,
    parentRunId: saFixture.parentRunId,
    childRunIds: saFixture.childRunIds,
  },
  {
    id: 'subagent-started-first',
    title: 'Sub-agents — started-first (live lifecycle)',
    description: 'Live agent-core lifecycle: parent streams subagent_started (subject = child) + child progress directly, with NO subagent_invoked/returned. Exercises the lazy findOrCreateSubAgent path — before the fix every child event here was dropped.',
    events: saStartedFirstFixture.events,
    parentRunId: saStartedFirstFixture.parentRunId,
    childRunIds: saStartedFirstFixture.childRunIds,
  },
  {
    id: 'ask-user-parked',
    title: 'Ask-user — parked runs (ask → completed → final_answer)',
    description:
      'Two runs in one conversation, each parking on ask_user with the runtime\'s real contract: ask_user_requested followed by a trailing completed (response = the question text) + final_answer_*. Run 1 asks one question and is answered; run 2 parks on FOUR questions — the prod-incident shape whose second card never rendered. Both question cards must render; neither turn may show "*The run finished without a response.*".',
    events: buildAskUserParked(),
  },
  {
    id: 'max-iterations-failure',
    title: 'Failed — iteration limit reached',
    description:
      'Two tool rounds, then agent.run.failed with code MAX_ITERATIONS (no final answer). The thread must collapse the CoT and show the "Iteration limit reached" alert instead of a silent blank message.',
    events: buildMaxIterationsFailure(),
  },
  {
    id: 'in-progress-no-final',
    title: 'In progress — no final answer yet',
    description: 'Reasoning + 2 tool_call/tool_result rounds, then stops. No final_answer_*, no completed. CoT timeline must stay expanded indefinitely.',
    events: buildInProgressNoFinal(),
  },
  {
    id: 'final-answer-started-no-text',
    title: 'In progress — final_answer_start, no text',
    description: 'Same prefix as in-progress-no-final + a single final_answer_start. CoT must collapse to "Thought for Xs" the instant the start event applies; no body text yet.',
    events: buildFinalAnswerStartedNoText(),
  },
  {
    id: 'final-answer-streaming',
    title: 'In progress — streaming final answer',
    description: 'final_answer_start + 3 deltas, no final_answer_stop / completed. CoT must be collapsed; answer text streams below the button. Verifies the visible separation between collapsed CoT button and streaming response.',
    events: buildFinalAnswerStreaming(),
  },
  {
    id: 'operator-delegate-final-only',
    title: 'Operator delegate — final_answer_stop only',
    description: "Real run (run-01kr6rhtd2zx8kmckwfg). Operator One delegates to Ops Analyst; no tool_calls, no reasoning_*, no final_answer_delta. Iters 0+1 each emit response_stop with isFinal:true (finalAnswerEnforcementRetries=2) before iter 2 finally produces final_answer_*. Tests that the CoT collapses on iter 2's final_answer_start and final_answer_stop.content is the authoritative response.",
    events: buildOperatorDelegateFinalOnly(),
  },
  {
    id: 'paper-analysis-architect',
    title: 'Architect — analyse paper-scout run',
    description: "Real Agent Architect delegated run (run-01kr7h1ew0z8r7san9yv). 85 events, ~2m 39s, 27 tool calls across Run Viewer / Files / Get Activity / Agent Editor / List User Activities. Activates the AUI Component Reference skill, creates a canvas + surface (multi-tab analysis), and produces the final answer via final_answer_stop. Good end-to-end smoke test for the new collapse behaviour on a long realistic trace.",
    events: buildPaperAnalysisArchitect(),
  },
  {
    id: 'hn-agent-analysis',
    title: 'Architect — analyse HN agent run',
    description: "Real Agent Architect run (run-01kr7j09csmqg5zt99q7). 25 events, ~52s. 9 Run Viewer tool calls, surface_update with a multi-card analysis, and final_answer_*. Mid-size happy-path trace.",
    events: buildHnAgentAnalysis(),
  },
];

/**
 * Committed capture fixtures — auto-discovered.
 *
 * Drop any run captured via `__eventDebug.exportFixture()` into this folder as
 * `cot-playback-capture-<name>.json` and it appears in the playback dropdown
 * automatically (no edit to this file). The JSON is the exact shape
 * exportFixture() emits: `{ id, title, description, events[], parentRunId?,
 * childRunIds? }`, events in chronological order. See
 * context/plan/the-cot-get-s-stuck-jaunty-kay.md.
 */
const CAPTURE_MODULES = import.meta.glob('./cot-playback-capture-*.json', {
  eager: true,
}) as Record<string, { default: Partial<PlaybackFixture> }>;

export const CAPTURE_FIXTURES: PlaybackFixture[] = Object.entries(CAPTURE_MODULES)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([path, mod]) => {
    const data = mod.default ?? {};
    const name = path.split('/').pop()!.replace(/^cot-playback-capture-/, '').replace(/\.json$/, '');
    const events = (data.events ?? []) as PlatformEvent[];
    return {
      id: data.id ?? `capture-${name}`,
      title: data.title ?? `Capture — ${name} (${events.length} events)`,
      description: data.description ?? `Captured run (${name}).`,
      events,
      parentRunId: data.parentRunId,
      childRunIds: data.childRunIds,
    };
  })
  .filter((f) => f.events.length > 0);

/**
 * Loads a fixture captured live via `__eventDebug.exportFixture()` from
 * localStorage (key {@link COT_FIXTURE_KEY}). Returns null when none is
 * present or the stored JSON is malformed. Used by CotPlaybackPage to offer an
 * "Imported (from capture)" option so a real stuck run can be replayed with no
 * source edit. See context/plan/the-cot-get-s-stuck-jaunty-kay.md.
 */
export function loadImportedFixture(): PlaybackFixture | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(COT_FIXTURE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PlaybackFixture>;
    if (!parsed || !Array.isArray(parsed.events) || parsed.events.length === 0) return null;
    return {
      id: parsed.id ?? 'imported-capture',
      title: parsed.title ?? `Imported (from capture) — ${parsed.events.length} events`,
      description: parsed.description ?? 'Captured live via __eventDebug.exportFixture().',
      events: parsed.events,
      parentRunId: parsed.parentRunId,
      childRunIds: parsed.childRunIds,
    };
  } catch {
    return null;
  }
}
